<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import BrandingSettingsAPI from 'dashboard/api/brandingSettings';
import SectionLayout from './SectionLayout.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

// SICARG MOD 1 — Identidad de marca (white-label) por cuenta.
const { t } = useI18n();
const store = useStore();

const companyName = ref('');
const primaryColor = ref('#1f93ff');
const logoFile = ref(null);
const faviconFile = ref(null);
const logoUrl = ref(null);
const faviconUrl = ref(null);
const isLoading = ref(false);

const loadSettings = async () => {
  try {
    const { data } = await BrandingSettingsAPI.get();
    companyName.value = data.company_name || '';
    primaryColor.value = data.primary_color || '#1f93ff';
    logoUrl.value = data.logo_url || null;
    faviconUrl.value = data.favicon_url || null;
  } catch (error) {
    // Sin configuración previa: se mantienen los valores por defecto
  }
};

onMounted(loadSettings);

const onLogoChange = event => {
  const [file] = event.target.files;
  logoFile.value = file || null;
};

const onFaviconChange = event => {
  const [file] = event.target.files;
  faviconFile.value = file || null;
};

const save = async () => {
  isLoading.value = true;
  try {
    const formData = new FormData();
    formData.append('branding_setting[company_name]', companyName.value || '');
    formData.append(
      'branding_setting[primary_color]',
      primaryColor.value || ''
    );
    if (logoFile.value) {
      formData.append('branding_setting[logo]', logoFile.value);
    }
    if (faviconFile.value) {
      formData.append('branding_setting[favicon]', faviconFile.value);
    }
    await BrandingSettingsAPI.update(formData);
    // Refrescar la cuenta para aplicar logo/favicon/título dinámicamente
    await store.dispatch('accounts/get');
    await loadSettings();
    logoFile.value = null;
    faviconFile.value = null;
    useAlert(t('GENERAL_SETTINGS.FORM.BRANDING.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.BRANDING.API.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const removeLogo = async () => {
  try {
    await BrandingSettingsAPI.deleteLogo();
    logoUrl.value = null;
    await store.dispatch('accounts/get');
    useAlert(t('GENERAL_SETTINGS.FORM.BRANDING.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.BRANDING.API.ERROR'));
  }
};

const removeFavicon = async () => {
  try {
    await BrandingSettingsAPI.deleteFavicon();
    faviconUrl.value = null;
    await store.dispatch('accounts/get');
    useAlert(t('GENERAL_SETTINGS.FORM.BRANDING.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.BRANDING.API.ERROR'));
  }
};
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.FORM.BRANDING.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.BRANDING.NOTE')"
    with-border
  >
    <form class="grid gap-4 mt-2" @submit.prevent="save">
      <WithLabel
        name="branding-company-name"
        :label="t('GENERAL_SETTINGS.FORM.BRANDING.COMPANY_NAME.LABEL')"
      >
        <NextInput
          v-model="companyName"
          type="text"
          class="w-full"
          :placeholder="
            t('GENERAL_SETTINGS.FORM.BRANDING.COMPANY_NAME.PLACEHOLDER')
          "
        />
      </WithLabel>

      <WithLabel
        name="branding-primary-color"
        :label="t('GENERAL_SETTINGS.FORM.BRANDING.PRIMARY_COLOR.LABEL')"
      >
        <div class="flex gap-2 items-center">
          <input
            v-model="primaryColor"
            type="color"
            class="w-10 h-9 rounded border cursor-pointer border-n-weak"
          />
          <NextInput v-model="primaryColor" type="text" class="w-40" />
        </div>
      </WithLabel>

      <WithLabel
        name="branding-logo"
        :label="t('GENERAL_SETTINGS.FORM.BRANDING.LOGO.LABEL')"
      >
        <div class="flex gap-3 items-center">
          <img
            v-if="logoUrl"
            :src="logoUrl"
            class="object-contain w-10 h-10 rounded bg-n-alpha-2"
          />
          <input type="file" accept="image/*" @change="onLogoChange" />
          <NextButton
            v-if="logoUrl"
            ghost
            ruby
            sm
            type="button"
            @click="removeLogo"
          >
            {{ t('GENERAL_SETTINGS.FORM.BRANDING.REMOVE') }}
          </NextButton>
        </div>
      </WithLabel>

      <WithLabel
        name="branding-favicon"
        :label="t('GENERAL_SETTINGS.FORM.BRANDING.FAVICON.LABEL')"
      >
        <div class="flex gap-3 items-center">
          <img
            v-if="faviconUrl"
            :src="faviconUrl"
            class="object-contain w-6 h-6 rounded bg-n-alpha-2"
          />
          <input type="file" accept="image/*" @change="onFaviconChange" />
          <NextButton
            v-if="faviconUrl"
            ghost
            ruby
            sm
            type="button"
            @click="removeFavicon"
          >
            {{ t('GENERAL_SETTINGS.FORM.BRANDING.REMOVE') }}
          </NextButton>
        </div>
      </WithLabel>

      <div>
        <NextButton blue :is-loading="isLoading" type="submit">
          {{ t('GENERAL_SETTINGS.SUBMIT') }}
        </NextButton>
      </div>
    </form>
  </SectionLayout>
</template>
